/**
 * Redeem an email-verification link (CLAUDE.md 11.6).
 *
 * The backend has minted these links and mailed them since Phase A1, and there was no
 * page to open -- every recipient landed on the 404. That is worse than not sending
 * the mail: a link in a transactional email that goes nowhere reads as a broken or
 * spoofed service.
 *
 * The token is redeemed once, on arrival, and is never stored. Resending needs a
 * session, because `POST /auth/email/resend` sends to the *signed-in* account rather
 * than to an address supplied by whoever opened the link -- which is what stops the
 * endpoint being a way to mail arbitrary people.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError, api } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'

type State =
  | { status: 'verifying' }
  | { status: 'done' }
  | { status: 'failed'; message: string }
  | { status: 'no_token' }

export function VerifyEmailPage() {
  const { t } = useTranslation(['auth', 'common'])
  const [searchParams] = useSearchParams()
  const { user, refreshUser } = useAuth()
  const token = searchParams.get('token') ?? ''

  const [state, setState] = useState<State>(() =>
    token ? { status: 'verifying' } : { status: 'no_token' },
  )
  const [resent, setResent] = useState(false)
  const [resending, setResending] = useState(false)

  // Redeemed once. A second attempt with a consumed token fails, so a re-render must
  // not repeat it -- in development, StrictMode would otherwise turn every successful
  // verification into a failure on the second pass.
  const attempted = useRef(false)

  useEffect(() => {
    if (!token || attempted.current) return
    attempted.current = true

    void (async () => {
      try {
        await api.publicPost('/auth/email/verify', { token })
        setState({ status: 'done' })
        // The signed-in user's `email_verified_at` just changed server-side.
        await refreshUser().catch(() => undefined)
      } catch (error) {
        setState({
          status: 'failed',
          message:
            error instanceof ApiError
              ? error.localised
              : t('errors:error.auth.verification_token_invalid'),
        })
      }
    })()
  }, [token, refreshUser, t])

  const onResend = async () => {
    setResending(true)
    try {
      await api.post('/auth/email/resend')
      setResent(true)
    } catch {
      // The endpoint answers the same way whether or not there is anything to send,
      // so there is nothing useful to report beyond the acknowledgement below.
      setResent(true)
    } finally {
      setResending(false)
    }
  }

  return (
    <AuthLayout title={t('email.verify_title')}>
      {state.status === 'verifying' && (
        <p role="status" className="text-center text-sm text-text-muted">
          {t('common:common.loading')}
        </p>
      )}

      {state.status === 'done' && (
        <div className="space-y-4 text-center">
          <CheckCircle2 size={32} className="mx-auto text-[var(--status-pass)]" aria-hidden />
          <p role="status" className="text-sm">
            {t('email.verify_done')}
          </p>
          <Button asChild variant="primary" className="w-full">
            <Link to="/">{t('common:nav.dashboard')}</Link>
          </Button>
        </div>
      )}

      {(state.status === 'failed' || state.status === 'no_token') && (
        <div className="space-y-4">
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            <XCircle size={16} className="mt-px shrink-0" aria-hidden />
            {state.status === 'failed'
              ? state.message
              : t('errors:error.auth.verification_token_invalid')}
          </p>

          {resent && (
            <p role="status" className="text-sm text-text-muted">
              {t('email.verify_sent')}
            </p>
          )}

          {user ? (
            <Button
              variant="primary"
              className="w-full"
              loading={resending}
              disabled={resent}
              onClick={() => void onResend()}
            >
              {t('email.resend')}
            </Button>
          ) : (
            // Resending needs a session: the endpoint mails the signed-in account, not
            // an address a stranger typed.
            <>
              <p className="text-sm text-text-muted">{t('common:verify.sign_in_to_resend')}</p>
              <Button asChild variant="primary" className="w-full">
                <Link to="/login">{t('login.submit')}</Link>
              </Button>
            </>
          )}
        </div>
      )}
    </AuthLayout>
  )
}
